import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "@maersk-global/mds-foundations/css/foundations.css";
import "@maersk-global/mds-design-tokens/maersk/light/css/design-tokens-px.css";
import "@maersk-global/fonts/maeu/fonts.css";

import "./styles/index.scss";

const API_BASES = [
  process.env.REACT_APP_AFR_DIGITAL_ASSISTANT_API_URL,
  process.env.REACT_APP_AFR_DIGITAL_ASSISTANT_CORE_URL,
].filter(Boolean);

const AUTH_TARGETS = API_BASES.map((base) => {
  try {
    const parsed = new URL(base);
    const normalizedPath = parsed.pathname.replace(/\/$/, "") || "/";
    return {
      origin: parsed.origin,
      path: normalizedPath,
    };
  } catch {
    return null;
  }
}).filter(Boolean);

const hasPathPrefixBoundary = (requestPath, allowedPath) => {
  if (allowedPath === "/") {
    return true;
  }

  return (
    requestPath === allowedPath || requestPath.startsWith(`${allowedPath}/`)
  );
};

const shouldAttachAuth = (url) => {
  if (!url || AUTH_TARGETS.length === 0) {
    return false;
  }

  try {
    const requestUrl = new URL(url, window.location.origin);
    return AUTH_TARGETS.some(
      (target) =>
        requestUrl.origin === target.origin &&
        hasPathPrefixBoundary(requestUrl.pathname, target.path)
    );
  } catch {
    return false;
  }
};

const getStoredToken = () => {
  try {
    return sessionStorage.getItem("authToken");
  } catch {
    return null;
  }
};

// Handle 401 response by clearing auth and redirecting to login
let isLoggingOut = false;
const handleUnauthorizedResponse = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  console.warn('Token expired or invalid — logging out user.');
  try {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("userInfo");
    sessionStorage.removeItem("oauth_state");
    sessionStorage.removeItem("graphToken");
    sessionStorage.removeItem("justAuthenticated");
    window.dispatchEvent(new Event('authStateChanged'));
  } catch (e) {
    // ignore storage errors
  }
  window.location.href = '/login-page';
};

const originalFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  const requestUrl = typeof input === "string" ? input : input?.url;
  const token = getStoredToken();

  if (!token || !shouldAttachAuth(requestUrl)) {
    return originalFetch(input, init);
  }

  const mergedHeaders = new Headers(
    init.headers || (input instanceof Request ? input.headers : undefined)
  );

  if (!mergedHeaders.has("Authorization")) {
    mergedHeaders.set("Authorization", `Bearer ${token}`);
  }

  const doFetch = input instanceof Request
    ? () => originalFetch(new Request(input, { ...init, headers: mergedHeaders }))
    : () => originalFetch(input, { ...init, headers: mergedHeaders });

  return doFetch().then((response) => {
    if (response.status === 401 && shouldAttachAuth(requestUrl)) {
      handleUnauthorizedResponse();
    }
    return response;
  });
};

axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (!token || !shouldAttachAuth(config.url || "")) {
    return config;
  }

  const headers = config.headers || {};
  const hasAuthHeader =
    Boolean(headers.Authorization) || Boolean(headers.authorization);

  if (!hasAuthHeader) {
    config.headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      handleUnauthorizedResponse();
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <main className="mds-container">
      <App />
    </main>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
